/**
 * `?next=` — preserving the intended destination across login.
 *
 * `RequireAuth` bounces a logged-out visitor to `/login`, and without this the
 * destination was dropped: login always landed on `/`, which `RootRedirect`
 * turns into `/trips`. Any deep link (a shared journey URL, a "open your trip"
 * link in a lifecycle email) silently lost its target.
 *
 * The value is attacker-controllable — it rides in a URL anyone can craft and
 * mail out — so it is treated as untrusted input and validated against a
 * same-origin allowlist before any redirect. Only site-relative paths pass:
 * anything that could leave the origin (absolute URLs, protocol-relative
 * `//evil.example`, backslash variants that some browsers normalise to `/`) is
 * rejected, which is what stops this from becoming an open redirect — the
 * classic phishing primitive, where a link on our own domain silently forwards
 * a just-authenticated user somewhere else.
 */

/** Auth pages themselves: allowing them as a target would bounce in a loop. */
const AUTH_PATHS = ['/login', '/signup'];

/**
 * Validate a `next` destination.
 *
 * @param raw The **already URL-decoded** param value (what
 *   `URLSearchParams.get` / `useSearchParams` returns). Passing a still-encoded
 *   string is safe — it simply fails the leading-`/` check and is rejected.
 * @returns The path to navigate to, or `null` when it must not be used.
 */
export function safeNextPath(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw === '') return null;
  // Must be site-relative: rules out "https://evil.example" and "javascript:…".
  if (!raw.startsWith('/')) return null;
  // "//evil.example" is protocol-relative — a full URL to another origin.
  if (raw.startsWith('//')) return null;
  // Browsers normalise backslashes to forward slashes in some positions, so
  // "/\evil.example" can resolve off-origin. Reject them outright.
  if (raw.includes('\\')) return null;
  const path = raw.split(/[?#]/)[0];
  if (AUTH_PATHS.includes(path)) return null;
  return raw;
}

/**
 * The login URL that remembers where the visitor was heading. Used by
 * `RequireAuth` when it turns an unauthenticated visit away.
 */
export function loginPathWithNext(location: { pathname: string; search?: string; hash?: string }): string {
  const target = `${location.pathname}${location.search || ''}${location.hash || ''}`;
  const safe = safeNextPath(target);
  return safe ? `/login?next=${encodeURIComponent(safe)}` : '/login';
}
